/**
 * Linguagem do contexto identity no front.
 *
 * Os tipos vêm do contrato gerado (`MeOut`, `TenantOut`): o front não
 * reescreve à mão o que o back já declarou.
 */
import type { components } from '@/core/http';

export type CurrentUser = components['schemas']['MeOut'];
export type Tenant = components['schemas']['TenantOut'];

/** Espelha `identity/domain/value_objects.py::UserStatus`. */
export const UserStatus = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  OFFBOARDED: 'offboarded',
} as const;

export function firstName(user: Pick<CurrentUser, 'full_name' | 'email'>): string {
  const name = user.full_name.trim();
  if (name) return name.split(/\s+/)[0] ?? name;
  return user.email.split('@')[0] ?? user.email;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Subtítulo do perfil: "Analista · Comercial". */
export function jobLine(user: Pick<CurrentUser, 'cargo' | 'area'>): string {
  return [user.cargo, user.area].filter(Boolean).join(' · ');
}

const CSS_COLOR = /^(#[0-9a-f]{3,8}|(rgb|hsl|oklch)a?\([^;{}]*\))$/i;

/**
 * Cor primária do tenant, se o branding trouxer uma válida.
 * Valida antes de injetar no CSS: o valor vem do banco, não do código.
 */
export function brandColor(tenant: Pick<Tenant, 'branding'>): string | undefined {
  const value = tenant.branding?.primary_color?.trim();
  return value && CSS_COLOR.test(value) ? value : undefined;
}

/**
 * Erros do identity que bloqueiam o acesso inteiro, não uma tela.
 * Os códigos são os de `identity/presentation/errors.py`.
 */
export const BLOCKING_ERRORS: Record<string, { title: string; message: string }> = {
  user_suspended: {
    title: 'Acesso suspenso',
    message: 'Seu acesso à plataforma foi suspenso. Procure o RH ou o administrador da sua empresa.',
  },
  tenant_inactive: {
    title: 'Empresa inativa',
    message: 'O acesso da sua empresa à plataforma está desativado no momento.',
  },
  tenant_not_found: {
    title: 'Empresa não encontrada',
    message: 'Sua conta não está vinculada a nenhuma empresa cadastrada.',
  },
  tenant_mismatch: {
    title: 'Sessão inconsistente',
    message: 'Sua sessão não corresponde à empresa cadastrada. Saia e entre novamente.',
  },
};
